import { RealtimeClient } from "@classdojo/web/pods/realtime";
import createAction from "@web-monorepo/infra/createAction";
import createActionCreator from "@web-monorepo/infra/createActionCreator";
import createAsyncActions from "@web-monorepo/infra/createAsyncActions";
import createReducer from "@web-monorepo/infra/createReducer";
import type { AnyAction } from "redux";
import { eventChannel, END } from "redux-saga";
import { delay, put, take, takeEvery, call } from "redux-saga/effects";
import { mountPoint, classId } from "src/pods/realtime/util/role";
import { navigate } from "src/utils/reactRouterNavigate";

// pairing

type State = {
  paired: boolean | null;
  displayOn: boolean | null;
};

const [FETCH_HOST_STATE, FETCH_HOST_STATE_DONE] = createAsyncActions("realtime/fetchHostState");
export const fetchHostState = createActionCreator(FETCH_HOST_STATE);
const fetchHostStateDone = (hasHost: boolean) => ({
  type: FETCH_HOST_STATE_DONE,
  payload: { hasHost },
});

export function createPairingSaga(client: RealtimeClient) {
  return function* pairingSaga() {
    // don't do anything until someone requests host state
    yield take(FETCH_HOST_STATE);

    const chan = eventChannel((emit) => {
      client.on("peerChange", () => {
        client.hasHostConnected().then((hasHost) => {
          emit(fetchHostStateDone(hasHost));
        });
      });

      client.on("close", () => emit(END));
      return () => {
        client.close();
      };
    });

    // do an initial check for host state
    const hasHost: boolean = yield call([client, client.hasHostConnected]);
    yield put(fetchHostStateDone(hasHost));

    while (true) {
      const action: AnyAction = yield take(chan);
      yield put(action);
    }
  };
}

export const pairedHandler = createReducer(FETCH_HOST_STATE_DONE, ({ hasHost }: { hasHost: boolean }) => {
  if (hasHost) {
    return (state: State): State => ({ ...state, paired: hasHost });
  } else {
    return (state: State): State => ({ ...state, paired: hasHost, displayOn: null });
  }
});

// display

export const DISPLAY_OPEN = createAction("DISPLAY_OPEN");
export const DISPLAY_CLOSE = createAction("DISPLAY_CLOSE");

export const displayOpen = createActionCreator(DISPLAY_OPEN, (path, action) => ({
  path,
  action,
}));
export const displayClose = createActionCreator(DISPLAY_CLOSE);

export const displayOpenHandler = createReducer(
  DISPLAY_OPEN,
  () =>
    (state: State): State => ({ ...state, displayOn: !!state.paired }),
);

export const displayCloseHandler = createReducer(
  DISPLAY_CLOSE,
  () =>
    (state: State): State => ({ ...state, displayOn: false }),
);

export const RECEIVE_DISPLAY_OPEN = createAction("RECEIVE_DISPLAY_OPEN");
export const RECEIVE_DISPLAY_CLOSE = createAction("RECEIVE_DISPLAY_CLOSE");

export function* displayNavSaga() {
  yield takeEvery(
    RECEIVE_DISPLAY_OPEN,
    function* ({ payload }: { type: string; payload: { action: AnyAction; path: string } }) {
      if (payload.action) yield put(payload.action);
      navigate(payload.path, { replace: true });
    },
  );

  yield takeEvery([RECEIVE_DISPLAY_CLOSE, DISPLAY_CLOSE], function* () {
    yield delay(30);
    navigate(`/apps/display`, { replace: true });
  });
}

export const SHARE_OVER_WIRE_SEND = createAction("SHARE_OVER_WIRE_SEND");
export const shareOverWire = (action: AnyAction) => ({
  type: SHARE_OVER_WIRE_SEND,
  payload: action,
});
export const SHARE_OVER_WIRE_RECEIVE = createAction("SHARE_OVER_WIRE_RECEIVE");
export function* overWireSaga() {
  yield takeEvery(SHARE_OVER_WIRE_RECEIVE, function* (action: { type: string; payload: AnyAction }) {
    yield put(action.payload);
  });

  yield takeEvery(SHARE_OVER_WIRE_SEND, function* (action: { type: string; payload: AnyAction }) {
    yield put(action.payload);
  });
}

// remote

export const REMOTE_CLOSE = createAction("REMOTE_CLOSE");
export const RECEIVE_REMOTE_CLOSE = createAction("RECEIVE_REMOTE_CLOSE");
export const remoteClose = createActionCreator(REMOTE_CLOSE);
export function* remoteNavSaga() {
  yield takeEvery([REMOTE_CLOSE, RECEIVE_REMOTE_CLOSE], function () {
    navigate(`/apps/remote/${mountPoint}/${classId}`, { replace: true });
  });
  if (mountPoint === "teach") {
    yield takeEvery([DISPLAY_OPEN], function* () {
      yield delay(30);
      navigate(`/apps/remote/${mountPoint}/${classId}`, { replace: true });
    });
  }
}
